import Vue from 'vue';
import axios from 'axios';
import VueAxios from 'vue-axios';
import get from "lodash/get";

import ENV from '@/config/environment';
import { readCookie, writeCookie, deleteCookie,
  getReferralCode, getInviteToken, getCampaignCode, getabTest } from '@/helpers/helpers';
import { redirect } from '@/helpers/helpersUsingStore';
import { updateAuthStatus } from '@/helpers/StoreActions';
import { store } from '@/store/store';
import { track, trackRegistration, ATTRIBUTION_KEYS } from '@/helpers/Tracker';
import { generate as generateEventId } from '@/helpers/ConversionId';
import { read as readAttribution } from '@/helpers/Attribution';
import GoogleRecaptchaInit from '@/helpers/Init/GoogleRecaptchaInit';

Vue.use(VueAxios, axios);

// PAE-2465: first-touch campaign context attached to signup requests and the registration event.
// Only the agreed keys are forwarded; the cookie's own bookkeeping (ts) never leaves the browser.
const attributionFields = () => {
  const stored = readAttribution() || {};
  const fields = {};
  ATTRIBUTION_KEYS.forEach((key) => {
    if(stored[key] !== undefined && stored[key] !== null && stored[key] !== '') {
      fields[key] = stored[key];
    }
  });
  return fields;
};

const authenticateWithTinybeans = async (credentials) => {
  updateAuthStatus({isRunning: true});
  const credentialsObj = { ...credentials };

  credentialsObj.clientId = ENV.clientId;
  credentialsObj.inviteToken = getInviteToken();

  try {
    const res = await authenticateWithServer(credentialsObj);
    if( get(res, 'accessToken') ) {
      res.rememberMe = credentialsObj.rememberMe;
      track('logIn');
      handleAuthSuccessful({res, source: 'access_token', isSignUp: false});
      return;
    }
    // successful authentication but no accessToken present.
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Sorry, please try a different username or password.'
    });
  } catch (error) {
    if ( 429 === error.status && error.data.captchaRequired ) { // response from server that there's too many login attempts
      token = await getGoogleCaptchaToken();
      if ( null === token || '' === token ) {
        updateAuthStatus({
          isRunning: false,
          hasFailed: true,
          message: 'Too many attempts for supplied email address! Please try to sign in later.'
        });
        return;
      }
      credentials.captchaToken = token;
      await authenticateWithTinybeans(credentials);
    } else {
      // catchall error handling
      updateAuthStatus({
        isRunning: false,
        hasFailed: true,
        message: 'Sorry, please try a different username or password.'
      });
    }
  }
};

const authenticateWithFacebook = async () => {
  updateAuthStatus({isRunning: true});
  const facebookAccessToken = readCookie('facebookAccessToken');
  if(facebookAccessToken) {
    await authenticateWithFacebookToken(facebookAccessToken);
  } else {
    FB.login(async function (fbResponse) {
      if (fbResponse.authResponse) {
        await authenticateWithFacebookToken(fbResponse.authResponse.accessToken);
      }
      else {
        updateAuthStatus({
          isRunning: false,
          hasFailed: true,
          message: 'Unexpected error! Please try again later.'
        });
      }
    }, {scope: 'public_profile,email'});
  }
};

const authenticateWithFacebookToken = async (facebookAuthToken) => {
  const attribution = attributionFields();
  const credentialsObj = {
    clientId: ENV.clientId,
    facebookAccessToken: facebookAuthToken,
    inviteToken: getInviteToken(),
    abTest: getabTest(),
    campaign: getCampaignCode(),
    referralCode: getReferralCode(),
    eventId: generateEventId(), // generated before the request so the server can reuse it (PAE-2465)
    ...attribution,
  };

  try {
    const res = await authenticateWithServer(credentialsObj);

    if(get(res, 'accessToken')) {
      writeCookie('facebookAccessToken', facebookAuthToken);
      if(get(res, 'didCreateUser')) {
        track('signUpFacebook');
        trackRegistration({ method: 'facebook', eventId: credentialsObj.eventId, attribution });
        handleAuthSuccessful({res, source: 'facebook', isSignUp: true});
      } else {
        track('logInFacebook');
        handleAuthSuccessful({res, source: 'facebook', isSignUp: false});
      }

      return;
    }
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  } catch (error) {
    console.error(error);
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  }

};

let token = '';
const signupWithTinybeans = async (credentials, attempts = 0 ) => {
  if(attempts > 2) {
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Too many attempts in an hour for supplied email address! Please try to sign up later.'
    });
    return;
  }

  updateAuthStatus({isRunning: true});

  if(readCookie('loginRedirect')) {
    credentials.source = 'link';
  }

  // Event ID is generated before the request and kept across a captcha retry (PAE-2465).
  if(!credentials.eventId) {
    credentials.eventId = generateEventId();
  }
  const attribution = attributionFields();

  try {
    const res = await Vue.axios.post(ENV.apis.signup, { ...credentials, ...attribution }, {
      headers: {
        'Content-Type': 'application/json'
      }
    });
    track('signUp');
    trackRegistration({ method: 'email', eventId: credentials.eventId, attribution });
    token = '';
    handleAuthSuccessful({res: res.data, source: 'access_token', isSignUp: true});
  } catch (error) {
    if(error.status === 429) {
      token = await getGoogleCaptchaToken();
      if(token == null || token == '') {
        updateAuthStatus({
          isRunning: false,
          hasFailed: true,
          message: 'Too many attempts in an hour for supplied email address! Please try to sign up later.'
        });
        return;
      }
      credentials.captchaToken = token;
      await signupWithTinybeans(credentials, attempts + 1);
    } else {
      const message = get(error, 'response.data.detailedMessage');
      updateAuthStatus({
        isRunning: false,
        hasFailed: true,
        message: message ? message :'Unexpected error! Please try again later.'
      });
    }
  }
};

const authenticateWithGoogle = async () => {
  updateAuthStatus({isRunning: true});
  try {
    const googleUser = await Vue.gAuth.signIn();
    const id_token = googleUser.getAuthResponse().id_token;

    if(id_token == null || id_token == '') {
      updateAuthStatus({
        isRunning: false,
        hasFailed: true,
        message: 'Unexpected error! Please try again later.'
      });
    }
    await authenticateWithGoogleToken(id_token);
  } catch (error) {
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  }
};

const authenticateWithGoogleToken = async (google_id_token) => {
  const attribution = attributionFields();
  const credentialsObj = {
    clientId: ENV.clientId,
    authProviderType: 'google',
    authProviderAccessToken: google_id_token,
    campaign: getCampaignCode(),
    inviteToken: getInviteToken(),
    eventId: generateEventId(),
    ...attribution,
  };

  if(readCookie('loginRedirect')) {
    credentialsObj.source = 'link';
  }

  try {
    const res = await authenticateWithServer(credentialsObj);
    if(get(res, 'accessToken')) {
      res.googleAccessToken = google_id_token; //Just try to follow sign in with facebook
      if(res.didCreateUser) {
        track('signUpGoogle');
        trackRegistration({ method: 'google', eventId: credentialsObj.eventId, attribution });
        handleAuthSuccessful({res, source: 'google', isSignUp: true});
      } else {
        track('logInGoogle');
        handleAuthSuccessful({res, source: 'google', isSignUp: false});
      }
      return;
    }
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  } catch (error) {
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  }
};

const authenticationWithApple = async () => {
  updateAuthStatus({isRunning: true});
  try {
    const response = await window.AppleID.auth.signIn();
    await authenticateWithAppleToken(response.authorization);
  } catch(error) {
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  }
};
const authenticateWithAppleToken = async (payload) => {
  const attribution = attributionFields();
  const credentialsObj = {
    clientId: ENV.clientId,
    campaign: getCampaignCode(),
    authProviderType: 'apple',
    doNotCreateUser: false,
    appleState: payload.state,
    idToken: payload.id_token,
    user: {
      name: {
        firstName: payload.firstName,
        lastName: payload.lastName,
      }
    },
    eventId: generateEventId(),
    ...attribution,
  };

  if(readCookie('loginRedirect')) {
    credentialsObj.source = 'link';
  }

  try {
    const res = await authenticateWithServer(credentialsObj);
    if(get(res, 'accessToken')) {
      if(get(res, 'didCreateUser')) {
        track('signUpApple');
        trackRegistration({ method: 'apple', eventId: credentialsObj.eventId, attribution });
        handleAuthSuccessful({res, source: 'apple', isSignUp: true});
      } else {
        track('logInApple');
        handleAuthSuccessful({res, source: 'apple', isSignUp: false});
      }
      return;
    }
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  } catch (error) {
    updateAuthStatus({
      isRunning: false,
      hasFailed: true,
      message: 'Unexpected error! Please try again later.'
    });
  }
};

const authenticateWithServer = async (credentialsObj) => {
  try {
    const res = await Vue.axios.post(ENV.apis.authentication, credentialsObj);
    const resData = res.data;
    return resData;
  } catch (error) {
    throw error.response;
  }
};

//Note
//???It seems source name has to match Ember authenticators' name
///Not sure what we set auenticator for, but just keep it
const handleAuthSuccessful = ({ res, source, isSignUp = false }) => {
  res.authenticator = `authenticator:${source}`;
  //not sure whether we should add extra property to store facebook/google token
  const payload = { authenticated: res };
  const rememberMe = get(res, 'rememberMe');
  if(rememberMe) {
    writeCookie('rememberMe', true);
    localStorage.setItem('tinybeansstore', JSON.stringify(payload));
  } else {
    localStorage.removeItem("tinybeansstore");
  }

  writeCookie('accessToken', res.accessToken);
  store.dispatch('updateAccessToken', res.accessToken);

  ['_src', 'ab-test', 'referral-code', 'campaign', 'abTest', 'referralCode'].map(deleteCookie);


  setTimeout(()=> {
    updateAuthStatus({isRunning: false});
  }, 1000);

  if(isSignUp) {
    beforeRedirect('/app/#/main/onboarding/step-start');
  } else if(res.didCreateUser){
    if(source == 'facebook' || source == 'google') {
      beforeRedirect('/app/#/main/account/confirm-details');
    } else if (source == 'apple'){
      beforeRedirect('/app/#/main/onboarding/step-start');
    }

  } else {
    // store.dispatch('updateUserStatus');
    beforeRedirect('/app/#');
  }
};

const getGoogleCaptchaToken = async () => {
  const sleep = (length) => new Promise(resolve => setTimeout(resolve, length));
  let newToken = '';
  let counter = 0;
  if(!document.getElementById('GoogleRecaptcha')) {
    GoogleRecaptchaInit();
    await sleep(3000);
    while (counter < 10) {
      if(grecaptcha == undefined) {
        counter++;
      } else {
        newToken = grecaptcha.getResponse();
        counter = 10;
      }
      await sleep(1000);
    }

  } else {
    grecaptcha.reset();
    grecaptcha.execute();

    while (counter < 10) {
      const token = grecaptcha.getResponse();
      if(token != '' && token != null) {
        newToken = token;
        counter = 10;
      } else {
        counter++;
      }
      await sleep(1000);
    }
  }
  return newToken;
};

const beforeRedirect = (destination) => {
  const loginRedirect = readCookie('loginRedirect');
  if(loginRedirect) {
    deleteCookie('loginRedirect');
    // Navigate directly to bypass redirect()'s 'from' param override
    if(parent !== window) {
      window.top.location.href = window.top.location.origin + loginRedirect;
    } else {
      window.location.href = window.location.origin + loginRedirect;
    }
    return;
  }
  redirect(destination);
};

export {
  authenticateWithTinybeans,
  authenticateWithFacebook,
  authenticateWithFacebookToken,
  signupWithTinybeans,
  authenticateWithGoogle,
  authenticateWithGoogleToken,
  authenticationWithApple,
  authenticateWithAppleToken,
  authenticateWithServer,
  handleAuthSuccessful
};
